import { useEffect } from "react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
const HEARTBEAT_INTERVAL_MS = 30 * 1000;
const HIDDEN_PAUSE_MS = 2 * 60 * 1000;
// A 401 here can be a false positive: the access token refresh is single-use
// server-side, so a burst of concurrent requests (e.g. right after a hidden
// tab regains focus, racing alongside React Query's refetch-on-focus) can
// cause this specific request to lose that race even though the session is
// fine. A short re-check avoids treating that as a real sign-out — by the
// retry, the request that won the race has already landed new cookies.
const RETRY_DELAY_MS = 2 * 1000;

export function useHeartbeat(enabled: boolean, onSignedOut?: () => void) {
  useEffect(() => {
    // When the user is signed out (`enabled === false`) we don't even start
    // the interval — this stops the request storm that an idle background
    // tab used to generate after the local session was cleared.
    if (!enabled) return;

    let cancelled = false;
    let interval: number | null = null;
    let hiddenSince: number | null = document.hidden ? Date.now() : null;

    const stop = () => {
      if (interval !== null) {
        window.clearInterval(interval);
        interval = null;
      }
    };

    const sendPing = () =>
      fetch(`${BASE}/api/auth/heartbeat`, { method: "POST", credentials: "include" });

    const ping = async () => {
      if (cancelled) return;
      // Skip the request entirely if the tab has been hidden long enough.
      if (
        document.hidden &&
        hiddenSince !== null &&
        Date.now() - hiddenSince > HIDDEN_PAUSE_MS
      ) {
        return;
      }
      try {
        const res = await sendPing();
        if (cancelled) return;
        if (res.status === 401) {
          // Don't trust a single 401 — re-check shortly before declaring the
          // session dead (see RETRY_DELAY_MS above for why).
          await new Promise((resolve) => window.setTimeout(resolve, RETRY_DELAY_MS));
          if (cancelled) return;
          const retryRes = await sendPing();
          if (cancelled) return;
          if (retryRes.status === 401) {
            // Server says we're signed out — let the auth layer handle it.
            // This same handler also broadcasts to other tabs via the auth
            // channel, so every tab in this browser flips to /login.
            stop();
            if (onSignedOut) onSignedOut();
          }
        }
      } catch {
        // Network errors are intentionally swallowed — a transient blip
        // shouldn't surface to the user or the error reporter.
      }
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        if (hiddenSince === null) hiddenSince = Date.now();
      } else {
        // Coming back from a long-hidden state: ping immediately so the
        // server records this user as active, then reset the timer baseline.
        const wasLongHidden =
          hiddenSince !== null && Date.now() - hiddenSince > HIDDEN_PAUSE_MS;
        hiddenSince = null;
        if (wasLongHidden) {
          void ping();
        }
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    void ping();
    interval = window.setInterval(ping, HEARTBEAT_INTERVAL_MS);

    return () => {
      cancelled = true;
      stop();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [enabled, onSignedOut]);
}
